import { useEffect, useState } from "react";
import { StreamChat, Channel as StreamChannel } from "stream-chat";
import {
  Channel,
  MessageList,
  MessageInput,
  Window,
  Thread,
} from "stream-chat-react";
import WorkspaceInfoCard from "./WorkspaceInfoCard";

interface Conversation {
  id: number;
  streamChannelId: string;
  bookingId: number;
  workspaceId: number;
  workspaceTitle: string;
  workspaceImageUrl: string | null;
  otherUserId: number;
  otherUserName: string;
  otherUserImage: string | null;
  lastMessageAt: string | null;
}

interface ConversationViewProps {
  conversation: Conversation;
  client: StreamChat;
  onBack: () => void;
}

export default function ConversationView({
  conversation,
  client,
  onBack,
}: ConversationViewProps) {
  const [channel, setChannel] = useState<StreamChannel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    let currentChannel: StreamChannel | null = null;

    const loadChannel = async () => {
      if (!client.userID) {
        console.error("Stream Chat client not connected");
        if (isMounted) {
          setError("Chat client not connected");
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const newChannel = client.channel("messaging", conversation.streamChannelId);
        await newChannel.watch();
        currentChannel = newChannel;

        if (isMounted) {
          setChannel(newChannel);
        }
      } catch (err) {
        console.error("Error loading channel:", err);
        if (isMounted) {
          setError("Failed to load conversation");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadChannel();

    return () => {
      isMounted = false;
      if (currentChannel) {
        currentChannel.stopWatching();
      }
    };
  }, [conversation.streamChannelId, client]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--secondary)]"></div>
      </div>
    );
  }

  if (error || !channel) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <svg
            className="w-12 h-12 mx-auto mb-3 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <p className="font-medium">{error || "Unable to load conversation"}</p>
          <button
            onClick={onBack}
            className="mt-3 text-sm text-blue-600 hover:underline"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header with back button (mobile) */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-200 md:hidden">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <span className="font-medium">{conversation.otherUserName}</span>
      </div>

      {/* Workspace Info Card */}
      <WorkspaceInfoCard
        workspaceId={conversation.workspaceId}
        workspaceTitle={conversation.workspaceTitle}
        workspaceImageUrl={conversation.workspaceImageUrl}
        bookingId={conversation.bookingId}
      />

      {/* Chat Area */}
      <div className="flex-1 overflow-hidden">
        <Channel channel={channel}>
          <Window>
            <MessageList />
            <MessageInput />
          </Window>
          <Thread />
        </Channel>
      </div>
    </div>
  );
}
